/**
 * Parse APQC-style hierarchy Excel file (Process or Capability).
 * Expected columns: Hierarchy ID, Name, Description (PCF ID optional, can be omitted).
 * Parent from hierarchy ID: x.0 = root, x.1/x.2 = children of x.0, x.1.1 = child of x.1.
 */

import * as XLSX from "xlsx";

const DEFAULT_HEADERS = {
  pcfId: ["pcf id", "pcfid", "id"],
  hierarchyId: ["hierarchy id", "hierarchyid", "hierarchy"],
  name: ["name"],
  elementDescription: ["element description", "elementdescription", "description"],
};

function findColumnIndex(headers, aliases) {
  const lower = headers.map((h) => String(h || "").trim().toLowerCase());
  for (let i = 0; i < lower.length; i++) {
    const h = lower[i];
    for (const alias of aliases) {
      if (h === alias.toLowerCase()) return i;
    }
  }
  return -1;
}

/**
 * Get parent hierarchy id from a hierarchy id string.
 * x.0 = root (null); x.1/x.2 = parent x.0 OR x (aliases); x.1.1 = parent x.1.
 * e.g. "9.0" -> null, "9.1" -> "9.0", "9.1.1" -> "9.1", "1.2" -> "1.0"
 * Excel often stores 1.0 as "1"; backend resolves both "1" and "1.0".
 */
export function getParentHierarchyId(hierarchyId) {
  if (hierarchyId == null) return null;
  const s = String(hierarchyId).trim();
  if (!s) return null;
  // x.0 (single top-level like 9.0, 10.0) = root
  if (/^\d+\.0$/.test(s)) return null;
  // Bare integer root (Excel often turns 1.0 into "1")
  if (/^\d+$/.test(s)) return null;
  const parts = s.split(".").filter((p) => p !== "");
  if (parts.length <= 1) return null;
  const parent = parts.slice(0, -1).join(".");
  // If parent is a single number (e.g. "9" from "9.1"), the parent node id is "9.0"
  // (APQC style). Backend also accepts bare "9" via aliases.
  return parent.includes(".") ? parent : `${parent}.0`;
}

/**
 * Alias keys for a hierarchy id so "1" and "1.0" resolve to the same parent.
 */
export function hierarchyIdAliases(hierarchyId) {
  const s = String(hierarchyId ?? "").trim();
  if (!s) return [];
  const aliases = [s];
  if (/^\d+\.0$/.test(s)) {
    aliases.push(s.slice(0, -2));
  } else if (/^\d+$/.test(s)) {
    aliases.push(`${s}.0`);
  }
  return aliases;
}

/**
 * Sort key for hierarchy ids so parents come before children.
 * e.g. 1.0, 1.1, 1.1.1, 1.1.1.1
 */
function hierarchySortKey(hierarchyId) {
  if (!hierarchyId || typeof hierarchyId !== "string") return [];
  return String(hierarchyId)
    .trim()
    .split(".")
    .map((p) => parseInt(p, 10) || 0);
}

/**
 * Parse an Excel file and return rows with { pcfId, hierarchyId, name, elementDescription, parentHierarchyId }.
 * Rows are sorted so parents appear before children.
 * @param {File} file - Excel file (.xlsx, .xls)
 * @returns {Promise<{ rows: Array<{ pcfId, hierarchyId, name, elementDescription, parentHierarchyId }>, error?: string }>}
 */
export function parseHierarchyExcel(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
          defval: "",
          raw: false,
        });
        if (!raw || raw.length < 2) {
          resolve({ rows: [], error: "File has no data or only headers." });
          return;
        }
        const headers = raw[0].map((h) => String(h ?? "").trim());
        const iPCF = findColumnIndex(headers, DEFAULT_HEADERS.pcfId);
        const iHier = findColumnIndex(headers, DEFAULT_HEADERS.hierarchyId);
        const iName = findColumnIndex(headers, DEFAULT_HEADERS.name);
        const iDesc = findColumnIndex(headers, DEFAULT_HEADERS.elementDescription);
        if (iName === -1 || iHier === -1) {
          resolve({
            rows: [],
            error: "Required columns not found. Expected: Hierarchy ID, Name, Description (PCF ID column optional).",
          });
          return;
        }
        const rows = [];
        for (let r = 1; r < raw.length; r++) {
          const row = raw[r] || [];
          const name = String(row[iName] ?? "").trim();
          const hierarchyId = String(row[iHier] ?? "").trim();
          if (!name && !hierarchyId) continue;
          rows.push({
            pcfId: iPCF >= 0 ? String(row[iPCF] ?? "").trim() : "",
            hierarchyId: hierarchyId || String(r),
            name: name || `Item ${r}`,
            elementDescription: iDesc >= 0 ? String(row[iDesc] ?? "").trim() : "",
            parentHierarchyId: getParentHierarchyId(hierarchyId),
          });
        }
        rows.sort((a, b) => {
          const ka = hierarchySortKey(a.hierarchyId);
          const kb = hierarchySortKey(b.hierarchyId);
          for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
            const va = ka[i] ?? 0;
            const vb = kb[i] ?? 0;
            if (va !== vb) return va - vb;
          }
          return 0;
        });
        resolve({ rows });
      } catch (err) {
        resolve({
          rows: [],
          error: err.message || "Failed to parse Excel file.",
        });
      }
    };
    reader.onerror = () =>
      resolve({ rows: [], error: "Failed to read file." });
    reader.readAsArrayBuffer(file);
  });
}
